import React, { useMemo, useState } from 'react';
import { useAppSelector } from '../../hooks/redux';
import { selectExportableEquipmentInstances } from '../../features/equipment/equipmentSelectors';
import { toolFoupsByTool, type ToolFoupOccupancy } from '../../features/simulation/toolPickupReady';
import { listSimulationTools } from '../../utils/listSimulationTools';
import CheckboxMultiSelect from '../common/CheckboxMultiSelect';
import SimulationAccordion from './SimulationAccordion';

function occupancyForTool(
  byTool: Record<string, ToolFoupOccupancy[]>,
  toolId: string,
  toolName: string
): ToolFoupOccupancy[] {
  const byId = byTool[toolId] ?? [];
  if (!toolName || toolName === toolId) return byId;
  const byName = byTool[toolName] ?? [];
  if (byName.length === 0) return byId;
  const seen = new Set(byId.map((entry) => `${entry.portId}::${entry.foupId}`));
  return [...byId, ...byName.filter((entry) => !seen.has(`${entry.portId}::${entry.foupId}`))];
}

function toolDropdownDetail(
  byTool: Record<string, ToolFoupOccupancy[]>,
  toolId: string,
  toolName: string
): string {
  return occupancyForTool(byTool, toolId, toolName).length > 0 ? 'Working' : 'Idle';
}

function statusClass(status: ToolFoupOccupancy['status']): string {
  return status === 'ready'
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-amber-100 text-amber-800';
}

/**
 * TOOL INVENTORY — which FOUP each process tool is holding, from live
 * `transfer_event` frames (processing vs pickup-ready).
 */
const ToolInventoryPanel: React.FC = () => {
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);
  const elements = useAppSelector((state) => state.layout.elements);
  const equipmentInstances = useAppSelector(selectExportableEquipmentInstances);
  const pickupReady = useAppSelector((state) => state.simulation.live.toolPickupReady);
  const simulationTime = useAppSelector((state) => state.simulation.simulationTime);

  const foupsByTool = useMemo(
    () => toolFoupsByTool(pickupReady, simulationTime),
    [pickupReady, simulationTime]
  );

  const tools = useMemo(
    () => listSimulationTools(equipmentInstances, Object.values(elements), Object.keys(foupsByTool)),
    [elements, equipmentInstances, foupsByTool]
  );

  const visibleTools = useMemo(() => {
    if (selectedToolIds.length === 0) return tools;
    const selected = new Set(selectedToolIds);
    return tools.filter((tool) => selected.has(tool.id));
  }, [selectedToolIds, tools]);

  const occupiedCount = visibleTools.reduce(
    (sum, tool) => sum + occupancyForTool(foupsByTool, tool.id, tool.name).length,
    0
  );

  return (
    <div className="space-y-2">
      <CheckboxMultiSelect
        label="Tools"
        noneLabel="All"
        value={selectedToolIds}
        options={tools.map((tool) => ({
          value: tool.id,
          label: tool.name,
          detail: toolDropdownDetail(foupsByTool, tool.id, tool.name),
        }))}
        onChange={setSelectedToolIds}
        labelClassName="mb-1 block text-[9px] font-semibold uppercase tracking-wider text-slate-400"
        buttonClassName="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-left text-[11px] font-medium text-slate-800 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-300"
      />

      <SimulationAccordion
        title="TOOL INVENTORY"
        ariaLabel="Tool inventory"
        defaultOpen
        headerExtra={
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-600">
            {occupiedCount} FOUP{occupiedCount === 1 ? '' : 'S'}
          </span>
        }
      >
        <div className="space-y-2 p-3">
          {visibleTools.length === 0 ? (
            <p className="text-[10px] text-slate-400">No process tools in this layout.</p>
          ) : (
            visibleTools.map((tool) => {
              const foups = occupancyForTool(foupsByTool, tool.id, tool.name);
              return (
                <article key={tool.id} className="rounded-lg border border-slate-200 p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-[11px] font-semibold text-slate-800">
                        {tool.name}
                      </div>
                      {tool.processType ? (
                        <div className="text-[9px] text-slate-400">{tool.processType}</div>
                      ) : null}
                    </div>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                        foups.some((foup) => foup.status === 'ready')
                          ? 'bg-emerald-100 text-emerald-700'
                          : foups.length > 0
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {foups.some((foup) => foup.status === 'ready')
                        ? 'Ready'
                        : foups.length > 0
                          ? 'Processing'
                          : 'Idle'}
                    </span>
                  </div>

                  {foups.length > 0 ? (
                    <ul className="mt-2 space-y-1">
                      {foups.map((foup) => (
                        <li
                          key={`${foup.portId}::${foup.foupId}`}
                          className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1.5"
                        >
                          <div className="min-w-0">
                            <div className="truncate font-mono text-[10px] font-semibold text-slate-800">
                              {foup.foupId}
                            </div>
                            <div className="truncate font-mono text-[9px] text-slate-400">
                              {foup.portId}
                            </div>
                          </div>
                          <span
                            className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${statusClass(foup.status)}`}
                          >
                            {foup.status === 'ready' ? 'Ready' : 'Processing'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1.5 text-[10px] text-slate-400">No FOUP on this tool.</p>
                  )}
                </article>
              );
            })
          )}
        </div>
      </SimulationAccordion>
    </div>
  );
};

export default ToolInventoryPanel;
